import Head from 'next/head';

import { renderBlocks } from '@/components/blocks';
import { Layout } from '@/components/layout/Layout';
import { JobListProvider } from '@/components/page/careers/context/JobListContext';
import { JobList } from '@/components/page/careers/JobList';
import {
  getAtsDepartments,
  getAtsJobPostings,
  getAtsLocations,
} from '@/lib/AtsApi';
import { BASE_REVALIDATE } from '@/lib/constants';
import { useDynamicYield } from '@/lib/dynamicYield';
import {
  latestPostsQuery,
  localeTranslationsQuery,
  navigationQuery,
  pageQuery,
  siteSettingsQuery,
} from '@/lib/queries';
import { getClient, readToken } from '@/lib/sanityUtils';
import { getValidLocale } from '@/lib/utils';

import type {
  SanityNavigationType,
  SanityPageType,
  SanitySiteSettingsType,
} from '@/types/sanity';
import type { Translation } from '@/types/shared';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';

const slug = 'careers';

export const getStaticProps = (async ({ locale, draftMode = false }) => {
  const client = getClient(draftMode ? { token: readToken } : undefined);

  const validLocale = getValidLocale(locale);

  if (!validLocale) {
    return {
      notFound: true,
    };
  }

  const careers = await client.fetch<SanityPageType>(
    pageQuery({ locale: validLocale, slug }),
  );

  const translations = await client.fetch<Translation[] | null>(
    localeTranslationsQuery(careers),
  );

  const siteSettings = await client.fetch<SanitySiteSettingsType>(
    siteSettingsQuery({ locale: validLocale }),
  );

  const navigation = await client.fetch<SanityNavigationType>(
    navigationQuery({ locale: validLocale }),
  );

  const [jobPostings, locations, departments] = await Promise.all([
    getAtsJobPostings(),
    getAtsLocations(),
    getAtsDepartments(),
  ]);

  const latestPostsData = await client.fetch(
    latestPostsQuery({ locale: validLocale }),
  );

  return {
    props: {
      careers,
      siteSettings,
      locale: validLocale,
      draftMode,
      jobPostings,
      locations,
      departments,
      latestPostsData,
      navigation,
      token: draftMode ? readToken : '',
      translations,
    },
    revalidate: Number(process.env.REVALIDATE || BASE_REVALIDATE),
  };
}) satisfies GetStaticProps;

const Careers = ({
  careers,
  siteSettings,
  locale,
  draftMode,
  jobPostings,
  locations,
  departments,
  latestPostsData,
  navigation,
  translations,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { recommendationContext } = useDynamicYield(locale, 'CATEGORY', [
    'About',
  ]);

  return (
    <Layout
      SEO={careers?.SEO}
      localeTranslations={translations}
      siteSettings={siteSettings}
      preview={draftMode}
      navigation={navigation}
      recommendationContext={recommendationContext}
    >
      <Head>
        {locale === 'en-GB' && (
          <>
            <script
              type="text/javascript"
              // eslint-disable-next-line
              dangerouslySetInnerHTML={{
                __html: `
                    var TTTH_DataLayer = {"UserID": "xxxx", "event": "ApplyNowClick"}
                    `,
              }}
            />
            <script
              type="text/javascript"
              // eslint-disable-next-line
              dangerouslySetInnerHTML={{
                __html: `
                    (function(e, s, t) {t = t || {}, t.id = e, versaTagObj = { $: [], onready: function (e) { this.$.push(e) } }; var n = s.getElementsByTagName("script")[0], r = s.createElement("script"); r.options = t, r.async = !0, r.src = "https://secure-ds.serving-sys.com/SemiCachedScripts/ebOneTag.js?id=" + e, r.options = t, n.parentNode.insertBefore(r, n)    })("1073746442", document, {dataLayer:"TTTH_DataLayer"    })
                    `,
              }}
            />
          </>
        )}
      </Head>

      {renderBlocks(careers?.blocks, {
        latestPostsData,
      })}
      <JobListProvider atsData={{ jobPostings, locations, departments }}>
        <JobList />
      </JobListProvider>
    </Layout>
  );
};

export default Careers;
